import { RedoButtonTabGroup } from "@redotech/arbiter/checkbox/redo-button-tab-group";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import {
  AdjustmentModeType,
  AdjustmentType,
  useItemAdjustments,
} from "@redotech/redo-customer-portal-shared/hooks/useItemAdjustments/use-item-adjustments";
import { MerchantOverrides } from "@redotech/redo-customer-portal-shared/hooks/useReview/use-merchant-overrides";
import { useTranslation } from "@redotech/redo-i18n";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import {
  SelectDropdown,
  SelectDropdownTheme,
} from "@redotech/redo-web/select-dropdown";
import * as reviewCss from "../review.module.css";

type AdjustmentModalTexts = NonNullable<MerchantOverrides>["texts"];

export interface MerchantOverrideAdjustmentModalDisplayProps {
  isOpen: boolean;
  onClose: () => void;
  texts: AdjustmentModalTexts;
  adjustmentType: AdjustmentType;
  adjustmentMode: AdjustmentModeType;
  adjustmentValue: string;
  isInputValid: boolean;
  onAdjustmentValueChange: (value: string) => void;
  formatInputValue: (value: string) => void;
  onAdjustmentTypeChange: (type: string) => void;
  onAdjustmentModeChange: (mode: string) => void;
  onApply: (
    adjustmentType: AdjustmentType,
    adjustmentMode: AdjustmentModeType,
  ) => void;
}

export const MerchantOverrideAdjustmentModalDisplay = ({
  isOpen,
  onClose,
  texts,
  adjustmentType,
  adjustmentMode,
  adjustmentValue,
  isInputValid,
  onAdjustmentValueChange,
  formatInputValue,
  onAdjustmentTypeChange,
  onAdjustmentModeChange,
  onApply,
}: MerchantOverrideAdjustmentModalDisplayProps) => {
  const { t } = useTranslation();

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: texts.adjustmentModalApplyButton,
        onClick: () => onApply(adjustmentType, adjustmentMode),
        size: "sm",
      }}
      secondaryButton={{
        text: texts.adjustmentModalCancelButton,
        onClick: onClose,
        size: "sm",
      }}
      title={texts.adjustmentModalTitle}
    >
      <Flex flexDirection="column" gap="3xl">
        <RedoButtonTabGroup<AdjustmentType>
          onChange={(value: AdjustmentType) => onAdjustmentTypeChange(value)}
          options={[
            { value: AdjustmentType.FEE, el: t("Fee") },
            { value: AdjustmentType.BONUS, el: t("Bonus") },
          ]}
          size="sm"
          value={adjustmentType}
        />
        <LabeledInput
          label={
            adjustmentMode === AdjustmentModeType.AMOUNT
              ? t("Amount")
              : t("Percentage")
          }
        >
          <RedoTextInput
            inputTypeHint="number"
            onBlur={() => formatInputValue(adjustmentValue)}
            setValue={onAdjustmentValueChange}
            size="md"
            state={isInputValid ? "default" : "error"}
            trailingActions={
              <SelectDropdown
                className={reviewCss.inlineCurrencyDropdown}
                display={(mode) => (
                  <Flex align="center" gap="xs">
                    <Text fontSize="sm" textColor="secondary">
                      {mode === AdjustmentModeType.AMOUNT ? "$" : "%"}
                    </Text>
                  </Flex>
                )}
                options={[
                  AdjustmentModeType.AMOUNT,
                  AdjustmentModeType.PERCENTAGE,
                ]}
                showChevron
                theme={SelectDropdownTheme.GHOST}
                value={adjustmentMode}
                valueChange={(mode) => mode && onAdjustmentModeChange(mode)}
              >
                {(mode) => (
                  <Text fontSize="xs" textColor="primary">
                    {mode === AdjustmentModeType.AMOUNT ? "$" : "%"}
                  </Text>
                )}
              </SelectDropdown>
            }
            value={adjustmentValue}
          />
        </LabeledInput>
      </Flex>
    </RedoModal>
  );
};

export const MerchantOverrideAdjustmentModal = ({
  overrides,
  isOpen,
  onClose,
  pendingReturnItemId,
  itemReturnValue,
}: {
  overrides: NonNullable<MerchantOverrides>;
  isOpen: boolean;
  onClose: () => void;
  pendingReturnItemId: string;
  itemReturnValue: number;
}) => {
  const { texts } = overrides;
  const {
    adjustmentType,
    adjustmentMode,
    adjustmentValue,
    onAdjustmentValueChange,
    isInputValid,
    onApply,
    formatInputValue,
    onAdjustmentTypeChange,
    onAdjustmentModeChange,
  } = useItemAdjustments({
    overrides,
    pendingReturnItemId,
    itemReturnValue,
    onClose,
  });

  return (
    <MerchantOverrideAdjustmentModalDisplay
      adjustmentMode={adjustmentMode}
      adjustmentType={adjustmentType}
      adjustmentValue={adjustmentValue}
      formatInputValue={formatInputValue}
      isInputValid={isInputValid}
      isOpen={isOpen}
      onAdjustmentModeChange={onAdjustmentModeChange}
      onAdjustmentTypeChange={onAdjustmentTypeChange}
      onAdjustmentValueChange={onAdjustmentValueChange}
      onApply={onApply}
      onClose={onClose}
      texts={texts}
    />
  );
};
